import { InvoiceSummaryWithCustomerJobData } from '../../../domain/invoicing/invoice'
import { StatementDetail, StatementSummary } from '../../../domain/invoicing/statement'
import { InvoiceListRowRef } from '../../../domain/invoicing/invoicingApi'

export type InvoiceRow = {
  rowType: 'invoice'
  key: string
  invoice: InvoiceSummaryWithCustomerJobData
}

export type ChildInvoiceRow = {
  rowType: 'childInvoice'
  key: string
  invoice: InvoiceSummaryWithCustomerJobData
  statement: StatementSummary
}

export type LoadingMembersRow = {
  rowType: 'loadingMembers'
  key: string
  statement: StatementSummary
}

export type StatementRow = {
  rowType: 'statement'
  key: string
  statement: StatementSummary
  children: (ChildInvoiceRow | LoadingMembersRow)[]
}

export type InvoiceListRow = InvoiceRow | StatementRow | ChildInvoiceRow | LoadingMembersRow

export const isRowClickable = (row: InvoiceListRow): boolean =>
  row.rowType === 'invoice' || (row.rowType === 'childInvoice' && !row.statement.wasEmailSent)

const toStatementRow = (statement: StatementSummary): StatementRow => ({
  rowType: 'statement',
  key: `statement-${statement.statementId}`,
  statement,
  children: statement.invoiceCount > 0
    ? [{ rowType: 'loadingMembers', key: `loading-${statement.statementId}`, statement }]
    : []
})

const toInvoiceRow = (invoice: InvoiceSummaryWithCustomerJobData): InvoiceRow => ({
  rowType: 'invoice',
  key: invoice.invoiceId,
  invoice
})

export const composeInvoiceListRows = (
  summaries: InvoiceSummaryWithCustomerJobData[],
  statements: StatementSummary[],
  rowRefs?: InvoiceListRowRef[]
): InvoiceListRow[] => {
  if (rowRefs) {
    const summaryById = new Map(summaries.map((summary): [string, InvoiceSummaryWithCustomerJobData] => [summary.invoiceId, summary]))
    const statementById = new Map(statements.map((statement): [string, StatementSummary] => [statement.statementId, statement]))
    return rowRefs.flatMap((ref): InvoiceListRow[] => {
      if (ref.type === 'statement') {
        const statement = statementById.get(ref.id)
        return statement ? [toStatementRow(statement)] : []
      }
      const invoice = summaryById.get(ref.id)
      return invoice ? [toInvoiceRow(invoice)] : []
    })
  }

  const statementRows = statements.map(toStatementRow)
  const standaloneInvoiceRows = summaries.filter(summary => !summary.statementId).map(toInvoiceRow)
  return [...statementRows, ...standaloneInvoiceRows]
}

export const expandedStatementIdsFromRows = (rows: InvoiceListRow[], expandedRowKeys: string[]): string[] =>
  rows.flatMap(row =>
    row.rowType === 'statement' && expandedRowKeys.includes(row.key) ? [row.statement.statementId] : []
  )

export const loadedMembersByStatementId = (
  statementIds: string[],
  results: { data?: StatementDetail }[]
): Map<string, InvoiceSummaryWithCustomerJobData[]> => {
  const membersByStatementId = new Map<string, InvoiceSummaryWithCustomerJobData[]>()
  statementIds.forEach((statementId, index) => {
    const invoices = results[index]?.data?.invoices
    if (invoices) {
      membersByStatementId.set(statementId, invoices)
    }
  })
  return membersByStatementId
}

export const applyLoadedStatementMembers = (
  rows: InvoiceListRow[],
  membersByStatementId: Map<string, InvoiceSummaryWithCustomerJobData[]>
): InvoiceListRow[] =>
  rows.map(row => {
    if (row.rowType !== 'statement') {
      return row
    }
    const members = membersByStatementId.get(row.statement.statementId)
    if (!members) {
      return row
    }
    return {
      ...row,
      children: members.map(invoice => ({
        rowType: 'childInvoice' as const,
        key: invoice.invoiceId,
        invoice,
        statement: row.statement
      }))
    }
  })
